import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Fade,
  TableFooter,
} from "@material-ui/core";
import { equals, find, map, pluck, propEq } from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import EditIcon from "@material-ui/icons/Edit";
import Typography from "@material-ui/core/Typography";
import YesNoCard from "../../components/YesNoCard";
import {
  useAllModules,
  useSubscriptions,
} from "../../manage-subscriptions/subscriptionService";
import CreateCurationIcon from "@material-ui/icons/PlaylistAdd";
import TablePagination from "@material-ui/core/TablePagination";
import { useSearchFilter } from "../../common/use-searchFilter";
import PropTypes from "prop-types";
import SubscriptionDialog from "../../manage-subscriptions/SubscriptionDialog";
import { useOrganizations } from "../../user-admin/userServices";
import { useSnackAlert } from "../../common/use-snackAlert";
import { useSubscriptionHandler } from "../../manage-subscriptions/use-SubscriptionHandler";
import Button from "@material-ui/core/Button";
import { useHistory, useLocation } from "react-router-dom";
import { DateTime } from "luxon";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 115,
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const initialState = {
  appliedOrganization: null,
  appliedModule: null,
  orderBy: "subscription.endDate",
  orderDirection: "asc",
  activeOnly: true,
  perpetualOnly: false,
  includeIndirect: true,
  appliedTypes: null,
  expiresBefore: null,
  page: 0,
};

const OrganizationSubscriptionsCard = ({ id }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [subscriptions, setSubscriptions] = useState([]);
  const [modules, setModules] = useState([]);
  const { SnackAlert, showAlert } = useSnackAlert();
  const [saveAlert, setSaveAlert] = useState(false);
  const timerAutoHide = React.useRef();
  const setAutoHideTimer = useCallback(timeoutDuration => {
    clearTimeout(timerAutoHide.current);
    timerAutoHide.current = setTimeout(() => {
      setSaveAlert(false);
    }, timeoutDuration);
  }, []);

  React.useEffect(() => {
    if (saveAlert) {
      setAutoHideTimer(3000);
    }

    return () => {
      clearTimeout(timerAutoHide.current);
    };
  }, [saveAlert, setAutoHideTimer]);

  const [searchState, setSearchState] = useState(initialState);
  const {
    state,
    handleUpdateSort,
    handleUpdatePage,
    handleUpdateOrganization,
  } = useSearchFilter(searchState);

  useEffect(() => {
    if (notBlank(id)) {
      handleUpdateOrganization(id);
    }
  }, [id, handleUpdateOrganization]);

  const modulesResponse = useAllModules();
  useEffect(() => {
    if (modulesResponse.isSuccess && notBlank(modulesResponse.data)) {
      setModules(modulesResponse.data);
    }
  }, [modulesResponse.isSuccess, modulesResponse.data]);

  const findModuleName = id => {
    const module = find(propEq("id", id), modules);
    return notBlank(module) ? module.name : "...";
  };

  const [organizations, setOrganizations] = useState([]);
  const allOrganizations = useOrganizations();
  useEffect(() => {
    if (allOrganizations.isSuccess && notBlank(allOrganizations.data)) {
      setOrganizations(allOrganizations.data);
    }
  }, [allOrganizations.isSuccess, allOrganizations.data]);

  const subResponse = useSubscriptions(
    searchState.appliedOrganization,
    searchState.appliedModule,
    searchState.includeIndirect,
    searchState.activeOnly,
    searchState.perpetualOnly,
    searchState.appliedTypes,
    searchState.expiresBefore,
    notBlank(searchState.orderBy)
      ? `${searchState.orderBy} ${searchState.orderDirection}`
      : null,
    searchState.page,
    4,
  );
  useEffect(() => {
    if (subResponse.isSuccess) {
      setSubscriptions(subResponse.data);
    }
  }, [subResponse.isSuccess, subResponse.data]);

  useEffect(() => {
    setSearchState(state);
  }, [state]);

  const afterSubscriptionSave = useCallback(
    async subs => {
      setSaveAlert(true);
      await subResponse.refetch();
    },
    [subResponse],
  );

  const afterSubscriptionDelete = useCallback(
    async subs => {
      await subResponse.refetch();
    },
    [subResponse],
  );

  const afterCurationCreate = useCallback(
    async subs => {
      showAlert("success", [`Curation Created.`]);
    },
    [showAlert],
  );

  const handleCurationCreateError = useCallback(
    err => {
      if (notBlank(err.body)) {
        showAlert("error", pluck("message", err.body.errors));
      } else {
        showAlert("error", [err.message]);
      }
    },
    [showAlert],
  );

  const handleRequestSort = useCallback(
    property => {
      const prop =
        property === state.orderBy && state.orderDirection === "desc"
          ? null
          : property;

      const direction =
        state.orderBy !== prop
          ? "asc"
          : state.orderDirection === "desc"
          ? "asc"
          : "desc";

      handleUpdateSort({
        property: prop,
        order: direction,
      });
    },
    [state, handleUpdateSort],
  );

  const {
    dialogOpen,
    currentSubscription,
    handleSubscriptionEditClick,
    handleNewSubscriptionClicked,
    handleSubscriptionDialogClose,
    handleSubscriptionDialogSave,
    handleSubscriptionDeleteClick,
    handleCreateCurationClick,
  } = useSubscriptionHandler({
    callbacks: {
      afterSave: afterSubscriptionSave,
      afterDelete: afterSubscriptionDelete,
      onCurationCreateError: handleCurationCreateError,
      afterCurationCreate: afterCurationCreate,
    },
  });

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <SnackAlert />

      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Active Subscriptions"
        action={
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Fade in={saveAlert}>
              <Box pr={5} color="green">
                Update Successful
              </Box>
            </Fade>
            <Tooltip title="Add Subscription">
              <IconButton onClick={handleNewSubscriptionClicked}>
                <AddIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {subResponse.isError && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            <Typography color="error">
              Error loading Organization Subscriptions.
            </Typography>
          </Box>
        )}
        {subResponse.isLoading && <PanelLoadingIndicator />}
        {subResponse.isSuccess && isBlank(subscriptions) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Active Subscriptions set up for this Organization.
          </Box>
        )}
        {subResponse.isSuccess && notBlank(subscriptions) && (
          <TableContainer>
            <Table stickyHeader aria-label="Expiring Subscriptions">
              <TableHead>
                <TableRow>
                  <TableCell key="module" align="left">
                    Module
                  </TableCell>

                  <TableCell key="type" align="left" className={classes.cell}>
                    <TableSortLabel
                      active={state.orderBy === "subscription.type"}
                      direction={state.orderDirection}
                      onClick={() => handleRequestSort("subscription.type")}
                    >
                      Type
                      {state.orderBy === "subscription.type" ? (
                        <span className={classes.visuallyHidden}>
                          {state.orderDirection === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="indirect"
                    align="center"
                    className={classes.cell}
                  >
                    Indirect
                  </TableCell>

                  <TableCell
                    key="start-date"
                    align="left"
                    className={classes.cell}
                  >
                    <TableSortLabel
                      active={state.orderBy === "subscription.startDate"}
                      direction={state.orderDirection}
                      onClick={() =>
                        handleRequestSort("subscription.startDate")
                      }
                    >
                      Start Date
                      {state.orderBy === "subscription.startDate" ? (
                        <span className={classes.visuallyHidden}>
                          {state.orderDirection === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="end-date"
                    align="left"
                    className={classes.cell}
                  >
                    <TableSortLabel
                      active={state.orderBy === "subscription.endDate"}
                      direction={state.orderDirection}
                      onClick={() => handleRequestSort("subscription.endDate")}
                    >
                      End Date
                      {state.orderBy === "subscription.endDate" ? (
                        <span className={classes.visuallyHidden}>
                          {state.orderDirection === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    style={{ width: 100 }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {map(sub => {
                  const moduleName = findModuleName(sub.mediaId);
                  return (
                    <TableRow hover tabIndex={-1} key={sub.id}>
                      <TableCell
                        key={"module:" + sub.id}
                        align="left"
                        style={{ minWidth: 450 }}
                      >
                        <Box>
                          <Typography noWrap variant="body1" component="div">
                            {moduleName}
                          </Typography>
                          <Box fontSize="1.2rem">({sub.mediaId})</Box>
                        </Box>
                      </TableCell>

                      <TableCell
                        key={"type:" + sub.id}
                        align="left"
                        className={classes.cell}
                      >
                        {sub.type}
                      </TableCell>

                      <TableCell
                        key={"indirect:" + sub.id}
                        align="center"
                        className={classes.cell}
                      >
                        <YesNoCard
                          value={sub.organizationId !== id}
                          showNoIcon={false}
                        />
                      </TableCell>

                      <TableCell
                        key={"start-date:" + sub.id}
                        align="left"
                        className={classes.cell}
                      >
                        {DateTime.fromISO(sub.startDate)
                          .toUTC()
                          .toLocaleString()}
                      </TableCell>

                      <TableCell
                        key={"end-date:" + sub.id}
                        align="left"
                        className={classes.cell}
                      >
                        {DateTime.fromISO(sub.endDate).toUTC().toLocaleString()}
                      </TableCell>

                      <TableCell
                        key={"actions:" + sub.id}
                        align="center"
                        style={{ width: 100 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                          pl={4}
                        >
                          {equals(sub.organizationId, id) && (
                            <Fragment>
                              <Tooltip title="Edit">
                                <IconButton
                                  classes={{ label: classes.icon }}
                                  onClick={() =>
                                    handleSubscriptionEditClick(sub)
                                  }
                                >
                                  <EditIcon color="primary" fontSize="large" />
                                </IconButton>
                              </Tooltip>

                              <ConfirmButton
                                handleDelete={() =>
                                  handleSubscriptionDeleteClick(sub)
                                }
                                dialogTitle="Remove Subscription"
                                dialogBody="Are you sure you want to remove this subscription?"
                              />
                            </Fragment>
                          )}
                          <Tooltip title="Create Curation">
                            <IconButton
                              classes={{ label: classes.icon }}
                              onClick={() => handleCreateCurationClick(sub)}
                            >
                              <CreateCurationIcon
                                color="primary"
                                fontSize="large"
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }, subscriptions)}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell>
                    <Button
                      variant="text"
                      onClick={() =>
                        history.push(`/manage-subscriptions`, {
                          organizationId: id,
                          previousLocation: location.pathname,
                        })
                      }
                    >
                      All Subscriptions
                    </Button>
                  </TableCell>
                  <TablePagination
                    classes={{
                      root: classes.pagination,
                    }}
                    rowsPerPageOptions={[4]}
                    colSpan={5}
                    count={-1}
                    rowsPerPage={4}
                    page={state.page}
                    onChangePage={(event, page) => handleUpdatePage(page)}
                    nextIconButtonProps={{
                      disabled: subscriptions.length < 4,
                    }}
                    labelDisplayedRows={({ page }) => `Page ${page + 1}`}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {dialogOpen && (
        <SubscriptionDialog
          open={dialogOpen}
          subscription={currentSubscription}
          organizationId={id}
          organizationList={organizations}
          moduleList={modules}
          onClose={handleSubscriptionDialogClose}
          onSave={handleSubscriptionDialogSave}
        />
      )}
    </Card>
  );
};

OrganizationSubscriptionsCard.propTypes = {
  id: PropTypes.string.isRequired,
};

export default OrganizationSubscriptionsCard;
